import React, { type PropsWithChildren, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, type View } from 'react-native';
import Animated, {
  interpolateColor,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';
import { BodyText } from '../../../../../Text';
import { NavbarNewContentIndicator } from '../NavbarNewContentIndicator';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingLink = (props: Props) => {
  const {
    accessibilityRole = 'link',
    accessibilityState,
    children,
    disabled,
    isActive = false,
    onPress,
    hasNewContent = false,
    ...restProps
  } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const linkRef = useRef<View>(null);
  const isHovered = useHovered(linkRef);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isDisabled = disabled === true;
  const isSelected = accessibilityRole === 'button' ? undefined : isActive;

  const mergedAccessibilityState = {
    ...accessibilityState,
    disabled: isDisabled,
    selected: isSelected,
  };

  // ─── Animated Values ───────────────────────────────────────────────────────

  const linkStateSV = useDerivedValue(() => {
    if (isActive) {
      return withTiming(LINK_STATE_ACTIVE, LINK_TIMING_CONFIG);
    }

    if (isHovered && !isDisabled) {
      return withTiming(LINK_STATE_HOVERED, LINK_TIMING_CONFIG);
    }

    return withTiming(LINK_STATE_IDLE, LINK_TIMING_CONFIG);
  });

  // ─── Animated Styles ───────────────────────────────────────────────────────

  const linkAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        linkStateSV.value,
        LINK_STATE_INPUT_RANGE,
        LINK_BACKGROUND_COLORS,
      ),
    };
  });

  const labelAnimatedStyle = useAnimatedStyle(() => {
    return {
      color: interpolateColor(linkStateSV.value, LINK_STATE_INPUT_RANGE, LINK_LABEL_COLORS),
    };
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const linkStyles = [styles.link, linkAnimatedStyle];
  const labelStyles = [styles.label, labelAnimatedStyle];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      accessibilityRole={accessibilityRole}
      accessibilityState={mergedAccessibilityState}
      activeOpacity={LINK_ACTIVE_OPACITY}
      disabled={isDisabled}
      onPress={onPress}
      {...restProps}
    >
      <Animated.View ref={linkRef} style={linkStyles}>
        <AnimatedBodyText
          maxFontSizeMultiplier={1.2}
          numberOfLines={1}
          sizeMatch={['16']}
          style={labelStyles}
        >
          {children}
        </AnimatedBodyText>
      </Animated.View>

      {hasNewContent ? <NavbarNewContentIndicator /> : null}
    </TouchableOpacity>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedBodyText = Animated.createAnimatedComponent(BodyText);

// ─── Constants ───────────────────────────────────────────────────────────────

const LINK_ACTIVE_OPACITY = 0.8;
const LINK_STATE_IDLE = 0;
const LINK_STATE_HOVERED = 1;
const LINK_STATE_ACTIVE = 2;

const LINK_STATE_INPUT_RANGE = [LINK_STATE_IDLE, LINK_STATE_HOVERED, LINK_STATE_ACTIVE];
const LINK_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.short };

const LINK_BACKGROUND_COLORS = [
  theme.colors.OPACITY.KALE.TRANSPARENT,
  theme.colors.OPACITY.BLACK.ALMOST_TRANSPARENT,
  theme.colors.OPACITY.KALE.LIGHTEST,
];

const LINK_LABEL_COLORS = [theme.colors.CHARCOAL, theme.colors.KALE, theme.colors.KALE];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    borderRadius: theme.radius['3xLarge'],
  },
  label: {
    color: theme.colors.CHARCOAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<
  {
    isActive?: boolean;
    hasNewContent?: boolean;
  } & Pick<
    TouchableOpacityProps,
    'accessibilityRole' | 'accessibilityState' | 'disabled' | 'onPress'
  >
>;
